import React, { forwardRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import AttachFile from '@material-ui/icons/AttachFile';

import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

const StyledMessageContainer = styled.div`
  width: 100%;
  margin-bottom: ${props => (props.isLast ? '2.5rem' : '0.5rem')};
  position: relative;
`;

const StyledBubbleContainer = styled.div`
  padding-left: ${props => (props.isFromSelf ? '5rem' : '2.5rem')};
  padding-right: ${props => (props.isFromSelf ? '2.5rem' : '5rem')};
  display: flex;
  flex-direction: ${props => (props.isFromSelf ? 'row-reverse' : 'row')};
  align-items: center;
`;

const StyledBubble = styled.div`
  background-color: ${props => (props.isFromSelf ? colors.ULTRAMARINE_BLUE : '#fff')};
  color: ${props => (props.isFromSelf ? '#fff' : 'inherit')};
  border: 1px solid;
  border-color: ${props => (props.isFromSelf ? 'transparent' : colors.ULTRAMARINE_BLUE)};
  padding: 0.5rem 1rem;
  display: inline-block;
  border-radius: 0.5rem;
  white-space: pre-line;
  text-align: ${props => (props.isFromSelf ? 'right' : 'left')}
    ${props => props.isFromSelf && props.isLast && `border-bottom-right-radius: 0;`};
  ${props => !props.isFromSelf && props.isLast && `border-bottom-left-radius: 0;`};
`;

const StyledAttachmentLink = styled.a`
  color: ${props => (props.isFromSelf ? '#fff' : colors.ULTRAMARINE_BLUE)};
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isFromSelf ? 'flex-end' : 'flex-start')};
`;

const StyledInfoContainer = styled.div`
  position: absolute;
  bottom: -1.5rem;
  display: ${props => (props.isLast ? 'flex' : 'none')};
  flex-direction: ${props => (props.isFromSelf ? 'row-reverse' : 'row')};
  align-items: flex-end;
  ${props => props.isFromSelf && `right: 0;`};
  ${props => !props.isFromSelf && `left: 0;`};
`;

const StyledAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center center;
`;

const StyledName = styled.div`
  padding: 0 0.5rem;
  font-size: 0.875rem;
  text-align: ${props => (props.isFromSelf ? 'right' : 'left')};
  color: ${colors.MEDIUM_GRAY};
  font-family: 'Circular-Bold';
`;

const StyledMessageTime = styled.span`
  font-family: 'Circular';
`;

const StyledIcon = styled(AttachFile)`
  width: 1rem !important;
  margin-right: 0.5rem;
`;

export const Message = forwardRef(({ message, id, ...rest }, ref) => {
  const {
    isLast,
    isFromSelf,
    data,
    date,
    author_avatar,
    author_name,
    attachment_url,
    attachment_name,
    marked_read_by,
  } = message;

  const markedReadBy = marked_read_by
    ? ` | ${getTranslatedString('messaging.markedReadyBy', { author: marked_read_by })}`
    : null;
  return (
    <StyledMessageContainer isLast={isLast} isFromSelf={isFromSelf} {...rest}>
      <StyledInfoContainer isLast={isLast} isFromSelf={isFromSelf}>
        <StyledAvatar src={author_avatar} />
        <StyledName id={id} ref={ref} isFromSelf={isFromSelf || markedReadBy}>
          {author_name} <StyledMessageTime>{moment(date).format('LT')}</StyledMessageTime>{' '}
          {markedReadBy}
        </StyledName>
      </StyledInfoContainer>
      <StyledBubbleContainer isFromSelf={isFromSelf}>
        <StyledBubble isLast={isLast} isFromSelf={isFromSelf}>
          {data.text}
          {attachment_url && (
            <StyledAttachmentLink
              isFromSelf={isFromSelf}
              href={message.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <StyledIcon /> {attachment_name}
            </StyledAttachmentLink>
          )}
        </StyledBubble>
      </StyledBubbleContainer>
    </StyledMessageContainer>
  );
});
